import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/subscription/permissions');
      setPermissions(res.data);
    } catch {
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    fetchPermissions();
  }, [fetchPermissions]);

  const hasAIAccess = permissions?.ai_access === true;
  const isOwner = permissions?.is_owner === true;
  const planName = permissions?.plan_name || 'Free';
  const plan = permissions?.plan || null;
  const usage = permissions?.usage || {};
  const subscriptionStatus = permissions?.subscription_status || '';

  const getRemaining = (type) => {
    const u = usage[type];
    if (!u) return 0;
    if (u.remaining === 'unlimited') return 999;
    return u.remaining || 0;
  };

  const getLimit = (type) => {
    const u = usage[type];
    if (!u) return 0;
    if (u.limit === 'unlimited') return 999;
    return u.limit || 0;
  };

  return (
    <PermissionsContext.Provider value={{
      permissions,
      loading,
      hasAIAccess,
      isOwner,
      planName,
      plan,
      usage,
      subscriptionStatus,
      getRemaining,
      getLimit,
      refresh: fetchPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider');
  return ctx;
}
